import { ChangeEvent, ReactNode, useEffect, useState } from 'react'
import { Button, Flex, Input, List, ListIcon, ListItem, Spinner, Text } from '@chakra-ui/react'
import InputField from '../InputField'
import { SearchIcon } from '@chakra-ui/icons'
import { UseFormSetValue } from 'react-hook-form'
import useApi from '../../common/utils/api/useApi'
import { AddressClient } from '../../pages/PatientsPage/AddressSlice'

type AddressSuggestionInputProps = {
  setValue: UseFormSetValue<any>
  tooltipContent?: ReactNode
  fieldMapping?: {
    address?: string
    postcode?: string
    state?: string
    suburb?: string
  }
  className?: string
  setIsShowAddressSuggestion?: (isShow: boolean) => void
}

const AddressSuggestionInput = ({
  setValue,
  tooltipContent,
  fieldMapping,
  className,
  setIsShowAddressSuggestion,
}: AddressSuggestionInputProps) => {
  const [query, setQuery] = useState<string | null>('')
  const [suggestions, setSuggestions] = useState([] as string[])
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [hoveredItem, setHoveredItem] = useState<null | number>(null)
  const [selectedAddress, setSelectedAddress] = useState('')

  const { trigger: getAddressSuggestions, loading } = useApi(AddressClient.getAddressSuggestions)
  const { trigger: getAddressDetails } = useApi(AddressClient.getAddressDetails)
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event) {
      console.error('event is missing')
      return
    }
    const { value } = event.target
    setQuery(value)
  }
  const handleItemHover = (index: number) => {
    setHoveredItem(index)
  }

  const handleItemLeave = () => {
    setHoveredItem(null)
  }
  const handleSelectAddress = (index: number) => {
    setSelectedAddress(suggestions[index] ?? '')
    setQuery(null)
  }

  useEffect(() => {
    if (!query) {
      setDebouncedQuery('')
      return
    }
    const debounceTimeout = setTimeout(() => {
      setDebouncedQuery(query.replace(/\//, ' '))
    }, 1000)

    return () => {
      clearTimeout(debounceTimeout)
    }
  }, [query])

  useEffect(() => {
    const fetchAddressSuggestions = async () => {
      if (debouncedQuery.trim() === '') {
        setSuggestions([])
        return
      }
      const suggestions: string[] = (await getAddressSuggestions(debouncedQuery)) ?? []
      setSuggestions(suggestions)
    }

    fetchAddressSuggestions()
  }, [debouncedQuery])

  useEffect(() => {
    if (!selectedAddress) return
    const fetchAddressDetailsBySuggestion = async () => {
      const addressDetails = await getAddressDetails(selectedAddress)
      setValue(fieldMapping?.address ?? 'address_1', addressDetails?.shortAddress ?? '')
      setValue(fieldMapping?.postcode ?? 'postcode', addressDetails?.postcode ?? '')
      setValue(fieldMapping?.state ?? 'state', addressDetails?.state ?? '')
      setValue(fieldMapping?.suburb ?? 'suburb', addressDetails?.suburb ?? '')
    }
    fetchAddressDetailsBySuggestion()
    setIsShowAddressSuggestion?.(false)
  }, [selectedAddress])

  return (
    <div className={className}>
      <InputField
        label="Address Search"
        tooltipContent={tooltipContent ?? 'e.g. 173 Whitehorse Rd Blackburn VIC, 3130'}
        useTooltipIcon
        tooltipIconColor="gray.800"
        field=""
      >
        <Input
          value={query === null ? selectedAddress : query}
          onChange={handleInputChange}
          placeholder="Enter an address"
          size="md"
          width="100%"
        />
        <Flex justifyContent="space-between" fontSize="sm">
          <Text color="gray.500">Select an address from the dropdown</Text>
          <Button
            variant="link"
            color="blue.500"
            onClick={() => setIsShowAddressSuggestion?.(false)}
            size="sm"
          >
            Can't find address? Enter manually
          </Button>
        </Flex>
      </InputField>
      <List bg="gray.50" borderRadius="md">
        {loading ? (
          <Flex align="center" justify="center" height="100px">
            <Spinner />
          </Flex>
        ) : (
          suggestions.map((suggestion, index) => (
            <ListItem
              key={suggestion}
              bg={hoveredItem === index ? 'gray.200' : 'transparent'}
              onMouseEnter={() => handleItemHover(index)}
              onMouseLeave={handleItemLeave}
              onClick={() => handleSelectAddress(index)}
              paddingY={2}
              paddingLeft={2}
            >
              <ListIcon as={SearchIcon} color="gray.400" />
              {suggestion}
            </ListItem>
          ))
        )}
      </List>
    </div>
  )
}

export default AddressSuggestionInput
